import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Modal } from '../../design-system/Modal';
import { Input, Textarea } from '../../design-system/Input';
import { Select } from '../../design-system/Dropdown';
import { Button } from '../../design-system/Button';
import { Plus, Globe, Lock } from 'lucide-react';

export const CreateProjectModal = ({ isOpen, onClose, onProjectCreated }) => {
  const { createProject, activeWorkspace } = useWorkspace();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Software Engineering');
  const [visibility, setVisibility] = useState('Public');
  const [error, setError] = useState('');

  const categoryOptions = [
    { value: 'Software Engineering', label: 'Software Engineering' },
    { value: 'Game Development', label: 'Game Development' },
    { value: 'Hardware Engineering', label: 'Hardware Engineering & BOM' },
    { value: 'Scientific Research', label: 'Scientific Research' },
    { value: 'Design & Creative', label: 'Design & Creative Studio' },
    { value: 'General Work', label: 'General Task Management' }
  ];

  const handleNameChange = (val) => {
    setName(val);
    if (!key || key === name.substring(0, 3).toUpperCase()) {
      const derived = val.replace(/[^a-zA-Z]/g, '').substring(0, 3).toUpperCase();
      setKey(derived || 'PRJ');
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!name.trim()) {
      setError('Project name is required.');
      return;
    }

    const newPrj = createProject({
      name: name.trim(),
      key: (key || name.substring(0, 3)).toUpperCase().trim(),
      description: description.trim(),
      category,
      visibility,
      workspaceId: activeWorkspace?.id
    });

    setName('');
    setKey('');
    setDescription('');
    setError('');
    onClose();

    if (onProjectCreated) {
      onProjectCreated(newPrj);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Project"
      subtitle={`Add a new tracked project inside ${activeWorkspace?.name || 'Workspace'}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} icon={<Plus size={16} />}>
            Create Project
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Input
          label="Project Name *"
          placeholder="e.g., Quantum Engine, Mobile Client V2"
          value={name}
          onChange={(e) => {
            handleNameChange(e.target.value);
            if (error) setError('');
          }}
          error={error}
          autoFocus
          required
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px' }}>
          <Input
            label="Project Key *"
            placeholder="e.g., QNT"
            value={key}
            onChange={(e) => setKey(e.target.value.toUpperCase())}
            helperText="Prefix for task IDs (e.g. QNT-101)"
            maxLength={6}
            required
          />

          <Select
            label="Discipline / Category"
            options={categoryOptions}
            value={category}
            onChange={setCategory}
          />
        </div>

        <Textarea
          label="Project Description"
          placeholder="Brief summary of project scope, objectives, and milestones..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />

        <div>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
            Project Visibility
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setVisibility('Public')}
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: visibility === 'Public' ? 'rgba(35, 190, 179, 0.12)' : 'var(--surface-recessed)',
                border: visibility === 'Public' ? '2px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Globe size={18} color="var(--accent-teal)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Public</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Visible to all members</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setVisibility('Private')}
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: visibility === 'Private' ? 'rgba(35, 190, 179, 0.12)' : 'var(--surface-recessed)',
                border: visibility === 'Private' ? '2px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Lock size={18} color="var(--accent-teal)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Private</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Only assigned members</div>
              </div>
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
